import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  Payment as PaymentIcon,
  MonetizationOn as MoneyIcon,
  CreditCard as CardIcon,
  AccountBalance as DebtIcon,
  Receipt as ReceiptIcon,
  PhoneIphone as PhoneIphoneIcon,
  TrendingUp as AdvanceIcon
} from '@mui/icons-material';
import { 
  recordPayment
} from '../../services/maintenanceCostService';
import { formatCurrency } from '../../utils/numberUtils';
import { MaintenanceRecord, MaintenancePaymentMethod } from '../../utils/types';

interface AdvancePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  maintenance: MaintenanceRecord;
  userId: string;
  userName: string;
  onSuccess: () => void;
}

const AdvancePaymentDialog: React.FC<AdvancePaymentDialogProps> = ({
  open,
  onClose,
  maintenance,
  userId,
  userName,
  onSuccess
}) => {
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<MaintenancePaymentMethod>('cash');
  const [walletNumber, setWalletNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const electronicMethods = ['vodafone_wallet', 'instapay', 'etisalat_wallet'];

  // حساب المعلومات المالية
  const currentPaid = maintenance.totalPaid || maintenance.paid || 0;
  const estimatedCost = maintenance.estimatedCost || maintenance.initialCost || 0;
  const maxAdvanceAmount = Math.max(0, estimatedCost - currentPaid);

  // تحديد نوع التكلفة المعروضة
  const costType = (maintenance.estimatedCost || 0) > 0 ? 'المقدرة' : 'المبدئية';

  // تحديث القيم عند فتح النافذة
  useEffect(() => {
    if (open && maintenance) {
      setPaymentAmount(0);
      setPaymentMethod('cash');
      setWalletNumber('');
      setNotes('دفعة مقدمة من مبلغ الصيانة');
      setError(null);
    }
  }, [open, maintenance]);

  const handleSubmit = async () => {
    if (paymentAmount <= 0) {
      setError('يجب إدخال مبلغ أكبر من صفر');
      return;
    }

    if (paymentAmount > maxAdvanceAmount) {
      setError(`المبلغ لا يمكن أن يتجاوز المبلغ المتاح: ${formatCurrency(maxAdvanceAmount)}`);
      return;
    }

    if (electronicMethods.includes(paymentMethod) && !walletNumber) {
      setError('يرجى إدخال رقم المحفظة أو الحساب المحول عليه');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // تحديد الملاحظات حسب طريقة الدفع
      let paymentNotes = 'دفعة مقدمة - ';
      
      switch (paymentMethod) {
        case 'cash':
          paymentNotes += 'نقدي';
          break;
        case 'vodafone_wallet':
          paymentNotes += `محفظة فودافون (${walletNumber})`;
          break;
        case 'instapay':
          paymentNotes += `انستاباي (${walletNumber})`;
          break;
        case 'etisalat_wallet':
          paymentNotes += `اتصالات كاش (${walletNumber})`;
          break;
        default:
          paymentNotes += 'نقدي';
      }

      if (notes) {
        paymentNotes += ` - ${notes}`;
      }

      // تسجيل الدفعة المقدمة
      await recordPayment(
        maintenance.id,
        paymentAmount,
        paymentMethod,
        userId,
        userName,
        paymentNotes
      );

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error recording advance payment:', error);
      setError('حدث خطأ أثناء تسجيل الدفعة المقدمة');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AdvanceIcon color="primary" />
          <Typography variant="h6">
            تسجيل دفعة مقدمة
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {maxAdvanceAmount <= 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              لا يمكن تسجيل دفعة مقدمة لأن المبلغ مدفوع بالكامل أو لا توجد تكلفة محددة.
            </Typography>
          </Alert>
        )}

        {/* معلومات الطلب */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'info.light' }}>
          <Typography variant="subtitle2" color="info.dark" gutterBottom>
            معلومات الطلب
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="info.dark">
                <strong>العميل:</strong> {maintenance.customerName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="info.dark">
                <strong>نوع الجهاز:</strong> {maintenance.deviceType}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="info.dark">
                <strong>التكلفة {costType}:</strong> {formatCurrency(estimatedCost)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="info.dark">
                <strong>المدفوع حالياً:</strong> {formatCurrency(currentPaid)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* طريقة الدفع */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>طريقة الدفع</InputLabel>
          <Select
            value={paymentMethod}
            onChange={(e) => {
              const method = e.target.value as MaintenancePaymentMethod;
              setPaymentMethod(method);
              if (!electronicMethods.includes(method)) {
                setWalletNumber('');
              }
            }}
            label="طريقة الدفع"
            disabled={loading}
          >
            <MenuItem value="cash">
              <Box display="flex" alignItems="center" gap={1}>
                <CardIcon fontSize="small" />
                <Box>
                  <Typography variant="body2">💵 نقدي</Typography>
                  <Typography variant="caption" color="text.secondary">
                    دفعة مقدمة نقدية
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
            <MenuItem value="vodafone_wallet">
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIphoneIcon fontSize="small" />
                <Box>
                  <Typography variant="body2">📱 فودافون كاش</Typography>
                  <Typography variant="caption" color="text.secondary">
                    تحويل عبر فودافون كاش
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
            <MenuItem value="instapay">
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIphoneIcon fontSize="small" />
                <Box>
                  <Typography variant="body2">💳 انستاباي</Typography>
                  <Typography variant="caption" color="text.secondary">
                    تحويل بنكي عبر انستاباي
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
            <MenuItem value="etisalat_wallet">
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIphoneIcon fontSize="small" />
                <Box>
                  <Typography variant="body2">📲 اتصالات كاش</Typography>
                  <Typography variant="caption" color="text.secondary">
                    تحويل عبر اتصالات كاش
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {/* مبلغ الدفعة */}
        <TextField
          label="مبلغ الدفعة المقدمة"
          type="number"
          fullWidth
          value={paymentAmount || ''}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            setPaymentAmount(Math.max(0, Math.min(value, maxAdvanceAmount)));
            setError(null);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MoneyIcon color="success" />
              </InputAdornment>
            ),
            endAdornment: <InputAdornment position="end">جنيه</InputAdornment>,
            inputProps: { min: 0, max: maxAdvanceAmount, step: 0.01 }
          }}
          sx={{ mb: 2 }}
          helperText={maxAdvanceAmount > 0 ? `الحد الأقصى: ${formatCurrency(maxAdvanceAmount)}` : 'لا يمكن تسجيل دفعة مقدمة'}
          disabled={loading || maxAdvanceAmount <= 0}
        />

        {/* رقم المحفظة */}
        {electronicMethods.includes(paymentMethod) && (
          <TextField
            label="رقم المحفظة/الحساب المحول عليه"
            fullWidth
            value={walletNumber}
            onChange={e => setWalletNumber(e.target.value)}
            placeholder={
              paymentMethod === 'vodafone_wallet' ? '01xxxxxxxxx' :
              paymentMethod === 'instapay' ? '1234567890123456' :
              '01xxxxxxxxx'
            }
            sx={{ mb: 2 }}
            disabled={loading}
            required
            helperText={`مطلوب لتأكيد ${
              paymentMethod === 'vodafone_wallet' ? 'تحويل فودافون كاش' :
              paymentMethod === 'instapay' ? 'التحويل البنكي' :
              'تحويل اتصالات كاش'
            }`}
          />
        )}

        {/* ملاحظات إضافية */}
        <TextField
          label="ملاحظات إضافية (اختياري)"
          fullWidth
          multiline
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          sx={{ mb: 2 }}
          disabled={loading}
          placeholder="أي ملاحظات إضافية حول الدفعة المقدمة..."
        />

        {/* ملخص الدفعة */}
        {paymentAmount > 0 && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light' }}>
            <Typography variant="subtitle2" color="success.dark" gutterBottom>
              ملخص الدفعة المقدمة
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="success.dark">
                  <strong>المبلغ المدفوع:</strong> {formatCurrency(paymentAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="success.dark">
                  <strong>المتبقي بعد الدفعة:</strong> {formatCurrency(maxAdvanceAmount - paymentAmount)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="success.dark">
                  <strong>إجمالي المدفوع:</strong> {formatCurrency(currentPaid + paymentAmount)} من {formatCurrency(estimatedCost)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || paymentAmount <= 0 || paymentAmount > maxAdvanceAmount || maxAdvanceAmount <= 0}
          startIcon={loading ? <CircularProgress size={20} /> : <ReceiptIcon />}
        >
          {loading ? 'جاري التسجيل...' : 'تسجيل الدفعة المقدمة'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancePaymentDialog;
